import { useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

/** Обновления фронта (Алексей 20.10, AI294 → #1639; правила 03.11 #8621).
 *  Фронт — PWA: новый билд = новый sw.js (skipWaiting + clientsClaim), браузер
 *  проверяет его раз в минуту (registration.update). Когда новый воркер взял страницу
 *  под контроль (controllerchange), на странице всё ещё старый код:
 *  - дашборд во вкладке браузера В ПОЛНОЭКРАННОМ режиме — НЕ перезагружаем сами: Chrome при
 *    перезагрузке выходит из fullscreen, возвращается адресная строка (#1637). Панель дашборда
 *    показывает кнопку «Пришли обновления — обновите страницу», после неё — «Обновлено успешно»
 *    и большую кнопку «Развернуть на весь экран» (флаг в sessionStorage);
 *  - всё остальное (установленная PWA, вкладка без fullscreen, другие страницы) — тихая
 *    перезагрузка, когда безопасно (не посреди диалога, вкладка видима);
 *  - форс-обновление: если в public/update-policy.json `force_before` позже метки нашей сборки
 *    (__BUILD_ID__), перезагружаемся даже из fullscreen — строка браузера вернётся, но режим
 *    «на весь экран» внутри приложения (без сайдбара и шапки) восстановит KioskContext.
 *    Включить: в update-policy.json поставить `force_before` = текущее время UTC (ISO) и
 *    выкатить; новая сборка получит метку позже и сама себя не перезагрузит. */
const CHECK_INTERVAL_MS = 60_000;
const RETRY_INTERVAL_MS = 15_000;
const JUST_UPDATED_KEY = 'app:justUpdated';
/** Перезагрузка по обновлению: KioskContext вернёт режим «на весь экран», если он был. */
export const RESTORE_KIOSK_KEY = 'app:restoreKiosk';
/** Текущий режим «на весь экран» во вкладке (пишет KioskContext) — снимок на момент перезагрузки. */
export const SESSION_KIOSK_KEY = 'app:kioskOn';
const DASHBOARD_PATH = '/usage/dashboard';
const POLICY_URL = '/update-policy.json';

let updateReady = false;
let forceWanted = false;
const listeners = new Set<() => void>();
function notify() { listeners.forEach((l) => l()); }
function setUpdateReady() {
  if (updateReady) return;
  updateReady = true;
  notify();
}
function setForceWanted() {
  if (forceWanted) return;
  forceWanted = true;
  notify();
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

type FsDoc = Document & { webkitFullscreenElement?: Element | null };
function browserFullscreen(): boolean {
  const d = document as FsDoc;
  return !!(d.fullscreenElement ?? d.webkitFullscreenElement);
}
function subscribeFs(l: () => void) {
  document.addEventListener('fullscreenchange', l);
  document.addEventListener('webkitfullscreenchange', l);
  return () => {
    document.removeEventListener('fullscreenchange', l);
    document.removeEventListener('webkitfullscreenchange', l);
  };
}
function useBrowserFullscreen(): boolean {
  return useSyncExternalStore(subscribeFs, browserFullscreen, () => false);
}

/** Открыто как установленная PWA (с экрана «Домой»): адресной строки нет, и перезагрузка
 *  не выбрасывает из полноэкранного — в отличие от вкладки браузера (#1637). */
export function isInstalledPwa(): boolean {
  try {
    if ((navigator as Navigator & { standalone?: boolean }).standalone) return true;
    return ['fullscreen', 'standalone', 'minimal-ui'].some((m) => window.matchMedia(`(display-mode: ${m})`).matches);
  } catch {
    return false;
  }
}

function useUpdateLoaded(): boolean {
  return useSyncExternalStore(subscribe, () => updateReady, () => false);
}
function useForceWanted(): boolean {
  return useSyncExternalStore(subscribe, () => forceWanted, () => false);
}

/** Показать ли кнопку «Пришли обновления — обновите страницу»: только вкладка браузера в
 *  полноэкранном режиме — там перезагрузка выбьет из fullscreen (Алексей 03.11 #8621). */
export function useUpdateReady(): boolean {
  const fs = useBrowserFullscreen();
  return useUpdateLoaded() && fs && !isInstalledPwa();
}

/** leftFullscreen — перезагрузка выбьет из fullscreen браузера: после неё нужно окно
 *  «Обновлено успешно» с кнопкой «Развернуть». Тихое обновление без fullscreen окна не показывает. */
function markReloadForUpdate(leftFullscreen: boolean) {
  try {
    if (leftFullscreen) sessionStorage.setItem(JUST_UPDATED_KEY, '1');
    if (sessionStorage.getItem(SESSION_KIOSK_KEY) === '1') sessionStorage.setItem(RESTORE_KIOSK_KEY, '1');
  } catch { /* приватный режим */ }
}

/** Применить обновление: перезагрузка с пометкой «это по обновлению». */
export function applyUpdate() {
  markReloadForUpdate(true);
  window.location.reload();
}

/** Одноразово: страница загружена по кнопке «обновите страницу» или форс-обновлением? Флаг снимается. */
export function consumeJustUpdated(): boolean {
  try {
    if (sessionStorage.getItem(JUST_UPDATED_KEY) !== '1') return false;
    sessionStorage.removeItem(JUST_UPDATED_KEY);
    return true;
  } catch {
    return false;
  }
}

function safeToReload(): boolean {
  return document.visibilityState === 'visible' && !document.querySelector('[role="dialog"]');
}

async function checkForcePolicy(registration?: ServiceWorkerRegistration) {
  try {
    const res = await fetch(`${POLICY_URL}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { force_before: forceBefore } = (await res.json()) as { force_before?: string | null };
    if (forceBefore && Date.parse(forceBefore) > Date.parse(__BUILD_ID__)) {
      setForceWanted();
      registration?.update().catch(() => { /* повторим через минуту */ });
    }
  } catch { /* файла нет или сеть — работаем как обычно */ }
}

export function useAutoReloadOnUpdate() {
  const { pathname } = useLocation();
  const ready = useUpdateLoaded();
  const force = useForceWanted();
  const fs = useBrowserFullscreen();

  // Регистрация воркера — сами (#1899): клиент плагина перезагружал страницу на
  // смене воркера. Здесь только register + проверка раз в минуту (воркер и форс-политика);
  // что делать с новым воркером, решает controllerchange ниже.
  useEffect(() => {
    if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
    let timer = 0;
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((registration) => {
      void checkForcePolicy(registration);
      timer = window.setInterval(() => {
        if (!navigator.onLine) return;
        registration.update().catch(() => { /* офлайн / сеть — попробуем через минуту */ });
        void checkForcePolicy(registration);
      }, CHECK_INTERVAL_MS);
    }).catch(() => { /* без воркера приложение работает как обычный сайт */ });
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // Первый controllerchange на свежей странице — установка воркера, а не обновление.
    const hadController = !!navigator.serviceWorker.controller;
    const onControllerChange = () => { if (hadController) setUpdateReady(); };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  // Кнопку ждём только на дашборде во вкладке браузера в fullscreen и без форса; иначе тихо.
  // Вышли из fullscreen с непринятым обновлением — fs меняется, эффект перезапустится и обновит.
  const manualOnly = pathname.startsWith(DASHBOARD_PATH) && fs && !isInstalledPwa() && !force;
  useEffect(() => {
    if (!ready || manualOnly) return;
    const reload = () => { markReloadForUpdate(browserFullscreen() && !isInstalledPwa()); window.location.reload(); };
    // Форс не ждёт закрытия диалогов: важное обновление важнее недописанной формы.
    const canReload = () => (force ? document.visibilityState === 'visible' : safeToReload());
    if (canReload()) { reload(); return; }
    const retry = window.setInterval(() => { if (canReload()) reload(); }, RETRY_INTERVAL_MS);
    return () => window.clearInterval(retry);
  }, [ready, manualOnly, force]);
}
